import { NgTemplateOutlet } from '@angular/common';
import { Component, ViewChild } from '@angular/core';
import { CompanyInformationComponent } from 'components/company/company-information.component';
import { ContactComponent } from 'components/company/contact/contact.component';
import { ListContactComponent } from 'components/company/contact/list-contact.component';
import { CustomInputComponent } from 'components/input/custom-input/custom-input.component';
import { FileUploadComponent, UploadedFile } from 'components/input/file-upload/file-upload';
import { SubmitButtonComponent } from 'components/input/submit-button/submit-button.component';
import { IconType } from 'components/layout/icon/icon.component';
import { ListColumnComponent, ListHeaderComponent, ListItemComponent, ListRowComponent, ListViewComponent, ListViewDirective } from 'components/layout/list-view/list-view.component';
import { ProductPlanTagConstants } from 'constants/ProductPlanTagConstants';
import { CredentialType } from 'enums/CredentialType';
import { InputType } from 'enums/InputType';
import { MicrosoftTenantModel } from 'models/generated/Distribution.Api.Models.MicrosoftTenant';
import { AccountingPlanModel } from 'models/generated/Entities.Accounting.AccountingPlan';
import { LedgerPostModel } from 'models/generated/Entities.Accounting.LedgerPost';
import { CompanyModel } from 'models/generated/Entities.GormCrm.Company';
import { ContactModel } from 'models/generated/Entities.GormCrm.Contact';
import { ProductPlanModel } from 'models/generated/Entities.GormCrm.ProductPlan';
import { TransferModel } from 'models/generated/Entities.GormCrm.Transfer';
import { TransferPlanModel } from 'models/generated/Entities.GormCrm.TransferPlan';
import { Layout } from 'modules/layout';
import { CurrencyPipe } from 'pipes/currency.pipe';
import { CustomDatePipe } from 'pipes/custom-date.pipe';
import { PaymentIntervalPipe } from 'pipes/payment-interval.pipe';
import { ProductNamePipe } from 'pipes/product-name.pipe';
import { TranslatePipe } from 'pipes/translate.pipe';
import { AccountingService } from 'services/accounting.service';
import { CompanyService } from 'services/company.service';
import { CredentialRequired } from 'services/credential.service';
import { CspService } from 'services/csp.service';
import { FileStorageService } from 'services/file-storage.service';
import { NavigationService } from 'services/navigation.service';
import { ProductPlanService } from 'services/productplan.service';
import { LegalCaseService } from 'services/legal-case.service';

@CredentialRequired(CredentialType.ProductPlanCompanyTransfer)
@Component({
	templateUrl: './transfer-wizard.component.html',
	imports: [Layout, ListViewComponent, ListItemComponent, CustomInputComponent, CompanyInformationComponent, ContactComponent, ListContactComponent, NgTemplateOutlet, FileUploadComponent, ListHeaderComponent, ListViewDirective, ListRowComponent, ListColumnComponent, CurrencyPipe, TranslatePipe, CustomDatePipe, PaymentIntervalPipe, ProductNamePipe]
})
export class TransferWizardComponent {
	@ViewChild('searchVatButton') searchVatButton!: SubmitButtonComponent;
	@ViewChild('uploadButton') uploadButton!: SubmitButtonComponent;
	@ViewChild('createTransferButton') createTransferButton!: SubmitButtonComponent;

	public steps = TransferWizardSteps;
	public currentStep: TransferWizardSteps = TransferWizardSteps.searchForTransferCompany;
	public loading = true;
	inputTypeEnum = InputType;

	// From-company
	public fromCompany?: CompanyModel;
	private _companyId?: string;
	set companyId(companyId: string | undefined) {
		this._companyId = companyId
		if (!companyId) return;
		this.loadCompanyData(companyId);
	}
	get companyId() { return this._companyId; }

	// To-company
	public searchVatNumber = '';
	public toCompanyExistsInGorm = false;
	public toCompany?: CompanyModel;
	public toCompanyMicrosoftTenant?: MicrosoftTenantModel;
	public newContact?: ContactModel;

	// Plans and invoices
	public productPlans: ProductPlanModel[] = [];
	public rootPlans?: ProductPlanModel[];
	public selectedPlans: ProductPlanModel[] = [];
	public ledgerPosts: LedgerPostModel[] = [];
	public activeAccountingPlans = new Map<ProductPlanModel, AccountingPlanModel>();

	// Uploads
	public uploadedFiles: UploadedFile[] = [];

	// Validation
	public validationWarningsToCompany: string[] = [];
	public validationErrorsFromCompany: string[] = [];
	public validationErrorsToCompany: string[] = [];
	public validationErrorsForPlans: string[] = [];

	// Transfer
	private transfer?: TransferModel;

	constructor(private readonly companyService: CompanyService,
		private readonly accountingService: AccountingService,
		private readonly legalCaseService: LegalCaseService,
		private readonly fileStorageService: FileStorageService,
		private readonly cspService: CspService,
		private readonly navigation: NavigationService,
		private readonly productPlanService: ProductPlanService) {
	}

	async loadCompanyData(companyId: string) {
		this.loading = true;
		let [_, company, plans] = await Promise.all([
			this.validateFromCompany(),
			this.companyService.getCompany(companyId),
			this.productPlanService.getAllProductPlansAndAccountingPlansFromCompany(companyId)
		]);
		this.ledgerPosts = await this.accountingService.getLedgerPostsByProductPlanIds(plans.map(p => p.id!));
		this.fromCompany = company;
		this.productPlans = plans;
		this.rootPlans = plans?.filter(plan => !plan.parent);
		plans?.forEach(plan => {
			this.activeAccountingPlans.set(plan, this.accountingService.filterActiveAccountingPlan(plan.accountingPlans));
			plan.show = this.showPlan(plan, !plan.parent);
		});

		if (this.rootPlans.length == 0)
			this.validationErrorsFromCompany.push('no-plans-eligable-for-transfer');
		this.loading = false;
	}

	showPlan(productPlan: ProductPlanModel, rootLevel: boolean = false) {
		return this.productPlanService.showPlan(productPlan, this.activeAccountingPlans, this.ledgerPosts, false, rootLevel);
	}

	public toggleSelectedPlan(plan: ProductPlanModel) {

		// Find top parent and always toggle all children recursively, to link all plans in the same hierarchy
		let topParent = plan;
		while (topParent.parent) topParent = topParent.parent;

		this.togglePlan(topParent);
		this.toggleChildPlans(topParent);

		this.validatePlanSelection();
	}
	private toggleChildPlans(plan: ProductPlanModel) {
		for (let child of plan.inverseParent) {
			this.togglePlan(child);
			this.toggleChildPlans(child);
		}
	}
	private togglePlan(plan: ProductPlanModel) {
		const currentIndex = this.selectedPlans.indexOf(plan);
		if (currentIndex >= 0) this.selectedPlans.splice(currentIndex, 1);
		else this.selectedPlans.push(plan);
	}

	getAnnualContractValue(plan: ProductPlanModel): number | undefined {
		const accountingPlan = this.activeAccountingPlans.get(plan);
		return this.accountingService.generateAnnualContractValue(accountingPlan?.startDate, accountingPlan?.endDate, accountingPlan?.totalPlanValue);
	}

	async validateFromCompany(): Promise<boolean> {
		// Ensure not in debt collection
		this.validationErrorsFromCompany = [];
		let inDebtCollections = await this.accountingService.getInDebtCollection([this.companyId!]);
		if (inDebtCollections.length > 0)
			this.validationErrorsFromCompany.push('debt-collection')
		return this.validationErrorsFromCompany.length == 0;
	}

	async validateToCompany(): Promise<boolean> {
		this.validationWarningsToCompany = [];
		this.validationErrorsToCompany = [];
		this.toCompanyExistsInGorm = !!this.toCompany?.id && (this.toCompany.id != "00000000-0000-0000-0000-000000000000");

		// Should exist client side at this time
		if (!this.toCompany) return false;

		// New company has no futher validation
		if (!this.toCompanyExistsInGorm) return true;

		let [inDebtCollections, settlementInfo, toCompanyMicrosoftTenant] = await Promise.all([
			this.accountingService.getInDebtCollection([this.toCompany!.id!]),
			this.legalCaseService.getSettlementByCompanyId(this.toCompany!.id!),
			this.cspService.getMicrosoftTenantByCompanyId(this.toCompany!.id!)
		]);	

		// Ensure we transfer to a different company
		if (this.toCompany!.id == this.companyId)
			this.validationErrorsToCompany.push('cannot-transfer-to-same-company')

		// Ensure not in debt collection
		if (inDebtCollections.length > 0)
			this.validationErrorsToCompany.push('debt-collection')

		// Ensure no active settlements for to-company
		if (settlementInfo.activeSettlement)
			this.validationWarningsToCompany.push('company-in-settlement')

		// Get tenant for later usage of plan selection
		this.toCompanyMicrosoftTenant = toCompanyMicrosoftTenant;

		return this.validationErrorsToCompany.length == 0;
	}

	validatePlanSelection(): boolean {
		this.validationErrorsForPlans = [];

		// At least one plan must be selected
		if (this.selectedPlans.length === 0) {
			return false;
		}

		// Only one web plan can be selected, due to DSv2 product structure, once DSv2 is removed this can be removed
		const webPlans = this.selectedPlans.filter(p => p.productPlanTags.find(t => t.tag == ProductPlanTagConstants.web || t.tag == ProductPlanTagConstants.webshop));
		if (webPlans.length > 1) {
			this.validationErrorsForPlans.push('only-one-web-plan');
		}

		// No open invoices on selected plans (handles debt collection as well and settlements)
		const selectedAccountingPlanIds = this.selectedPlans.flatMap(p => p.accountingPlans).map(a => a.id!);
		const postsForSelectedPlans = this.ledgerPosts.filter(l => selectedAccountingPlanIds.find(id => id == l.accountingPlanId));
		if (postsForSelectedPlans.find(p => p.ledgerHeader && !p.ledgerHeader.closed)) {
			this.validationErrorsForPlans.push('open-invoice')
		}

		// Full hierarchies		
		let children = this.selectedPlans.filter(plan => plan.parentId);
		children.forEach(child => {
			if (!this.selectedPlans.find(p => p.id == child.parentId))
				this.validationErrorsForPlans.push('missing-parentplan')
		});
		let parents = this.selectedPlans.filter(plan => plan.inverseParent?.length > 0);
		parents.forEach(parent => {
			parent.inverseParent.forEach(child => {
				if (!this.selectedPlans.find(p => p.id == child.id))
					this.validationErrorsForPlans.push('error.missing-childplan')
			});
		});

		// Subscriptions
		const selectedSubscriptionPlans = this.selectedPlans.filter(p => p.productPlanTags.find(t => t.tag == ProductPlanTagConstants.microsoft365));
		if (selectedSubscriptionPlans.length > 0) {
			// No tenant move if to-company already has a tenant
			if (this.toCompanyMicrosoftTenant)
				this.validationErrorsForPlans.push('tenant-already-exists')

			// All active subscriptions must move if any
			const activeSubscriptionPlans = this.productPlans
				.filter(p => p.productPlanTags.find(t => t.tag == ProductPlanTagConstants.microsoft365))
				.filter(p => p.autoRenewable && p.autoRenewalDate && p.deliveryDate && p.deliveryDate <= new Date() && p.autoRenewalDate > new Date());
			if (selectedSubscriptionPlans.length < activeSubscriptionPlans.length)
				this.validationErrorsForPlans.push('all-or-none-microsoft365-plans-must-move')
		}

		return this.validationErrorsForPlans.length === 0;
	}

	completeUpload() {
		this.createTransfer();
		this.nextStep();
	}

	createTransfer(): TransferModel {
		this.transfer = new TransferModel();
		this.transfer.transferPlans = [];
		this.transfer.fromCompanyId = this.companyId;
		this.transfer.toCompanyId = this.toCompany?.id;
		this.transfer.fileReferenceId = this.uploadedFiles[0].fileReference?.id
		this.selectedPlans.forEach(plan => {
			let transferPlan = new TransferPlanModel();
			transferPlan.productPlanId = plan.id;
			this.transfer!.transferPlans.push(transferPlan);
		});
		return this.transfer;
	}

	async searchVat() {
		this.toCompany = await this.companyService.getCompanyFromVatNumber(this.searchVatNumber);
		if (this.toCompany && this.toCompany.companyContacts.length === 0) this.newContact = this.companyService.getNewContact();

		await this.validateToCompany();
		this.searchVatButton.reset();
		this.nextStep();
	}

	downloadTransferDeclaration() {
		const filePath = this.uploadedFiles[0].filePath;
		if (filePath)
			this.fileStorageService.downloadFile(filePath);
	}

	getTransferDeclarationFileName() {
		return this.uploadedFiles[0].fileReference?.fileName;
	}

	getIcon(plan: ProductPlanModel): IconType {
		const forbiddenIcon = "exclamation-triangle";
		const canBeSelectedIcon = "square-check";
		const forReview = "square-full";

		if (this.currentStep == TransferWizardSteps.reviewTransfer)
			return forReview;

		if (!this.planCanBeMoved(plan))
			return forbiddenIcon;

		return canBeSelectedIcon;
	}

	getStatusColor(plan: ProductPlanModel): string {
		if (!this.planCanBeMoved(plan))
			return 'color-red-50';

		const isSelected = this.selectedPlans.find(p => p.id == plan.id);
		if (isSelected || this.currentStep == TransferWizardSteps.reviewTransfer)
			return 'color-green-50';
		return 'color-grey-40';
	}

	planCanBeMoved(plan: ProductPlanModel): boolean {
		// plan has a subscription but to-company already has a tenant
		if (this.toCompanyMicrosoftTenant && plan.productPlanTags.find(t => t.tag == ProductPlanTagConstants.microsoft365)) return false;

		// plan has an open invoice 
		const accountingPlanIds = this.productPlans.filter(p => p.id == plan.id).flatMap(p => p.accountingPlans).map(a => a.id!);
		const openPosts = this.ledgerPosts.filter(p => p.ledgerHeader && !p.ledgerHeader.closed && accountingPlanIds.find(id => id == p.accountingPlanId));
		if (openPosts.length > 0) return false;

		// plan is not started - this plan can be moved but only if it has a child that can be moved
		if (!plan.deliveryDate && !plan.inverseParent?.filter(child => this.planCanBeMoved(child)).length) return false;

		// plan is old oneoff product with no parents
		if (!plan.autoRenewable && !plan.parent) {
			const futurePosts = this.ledgerPosts.filter(p => p.isFutureInvoice && accountingPlanIds.find(id => id == p.accountingPlanId));
			if (futurePosts.length == 0) return false;
		}

		return true;
	}

	async completeTransfer() {
		if (!this.transfer || !this.toCompany) return;

		try {
			// Create company
			if (!this.toCompanyExistsInGorm) {
				this.toCompany.primaryContact = this.newContact;				
				this.toCompany = await this.companyService.createCompany(this.toCompany!);
				this.transfer.toCompanyId = this.toCompany.id;
			}
			// Create transfer
			this.transfer = await this.companyService.transferPlans(this.transfer!);
			this.navigation.closeView(this);
		} finally {
			this.createTransferButton.reset();
		}
	}

	nextStep() {
		this.changeStep(1);
	}

	previousStep() {
		this.changeStep(-1);
	}

	changeStep(val: number) {
		const currentIndex = TransferWizardSteps.getKeys().indexOf(this.currentStep);
		if (currentIndex + val > this.steps.getKeys().length - 1 || currentIndex + val < 0) return;
		this.currentStep = TransferWizardSteps.getKeys()[currentIndex + val];
	}

	isNewContactValid() {
		if (!this.newContact) return false;
		return this.companyService.isContactValid(this.newContact, true);
	}
}

enum TransferWizardSteps {
	searchForTransferCompany, confirmCompany, selectPlans, uploadDocument, reviewTransfer
}
namespace TransferWizardSteps {
	export function getKeys(): TransferWizardSteps[] {
		return Object.values(TransferWizardSteps).map(v => v.toString()).reduce((pre: string[], cur: string, i, arr: string[]) => {
			const index = i % (arr.length >> 1);
			if (!pre[index]) {
				pre[index] = cur;
			}
			return pre;
		}, []).map((val: string) => { return TransferWizardSteps[val as keyof typeof TransferWizardSteps] as TransferWizardSteps });
	}
}
